export type ResampleSize = number;
export type ScoutAxis = 'coronal' | 'sagittal';

/** Minimum allowed slice thickness in mm. */
export const MIN_THICKNESS_MM = 1.0;
/** Maximum allowed slice thickness in mm. */
export const MAX_THICKNESS_MM = 5.0;
/** Minimum allowed field of view in mm. */
export const MIN_FOV_MM = 50;
/**
 * Maximum allowed field of view in mm.
 *
 * Larger than the reconstruction fields of view this tool is used on, and
 * low enough to bound the output matrix and memory when the field of view
 * pads past the source extent.
 */
export const MAX_FOV_MM = 850;
/**
 * Maximum scatter-extension margin per end, mm. Bounds the slices, memory
 * and resample time an extension can cost.
 */
export const MAX_EXTENSION_MARGIN_MM = 100;
/**
 * Minimum scatter-extension margin per end, mm. Also anchors the input's
 * 1 cm step on whole centimetres rather than on 0.1, 1.1, 2.1.
 */
export const MIN_EXTENSION_MARGIN_MM = 10;
/** Margin the scatter extension opens at, mm. */
export const DEFAULT_EXTENSION_MARGIN_MM = 50;
/**
 * Least of the field of view, per in-plane axis, that must stay over the
 * source when the region is moved.
 *
 * The region may sit off the source's centre — that is what moving it is
 * for, and a field of view reaching past the acquisition is a supported
 * result — but a region that has left the image entirely frames nothing.
 * Before this the in-plane centre was unbounded, so a drag could carry the
 * region arbitrarily far and the scouts, which fit the union of source and
 * region, zoomed out until the anatomy was a speck.
 *
 * Where the source is narrower than this fraction of the field of view the
 * overlap cannot be met at any centre; the rule then degrades to bounding
 * the travel rather than guaranteeing the proportion.
 */
export const MIN_SOURCE_OVERLAP = 0.2;

/** Minimum number of slices in the output volume. */
export const MIN_SLICES = 3;

/**
 * Most voxels an output may hold.
 *
 * At two bytes each this is 2 GB of pixels, and the pipeline holds several
 * copies transiently — the assembled source, the resampled output, and the
 * per-slice buffers the writer cuts from it. Above this the allocation
 * fails inside the resample, after the UI has already frozen for the time
 * it took to get there, and arrives as a generic error. Refusing on the
 * count instead names it before anything is allocated.
 */
export const MAX_OUTPUT_VOXELS = 1_000_000_000;
